import classNames from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { ReactElement, ReactNode } from 'react'

import { Translation } from 'cng-common/features/translations'
import { PartnerNameWithLink } from 'partner360/components/partnerNameWithLink'
import {
  EMPTY_COCKPIT_VALUE,
  IRevenueAndSobHierarchyNode,
  TRevenueAndSobHierarchyLevel,
  TRevenueAndSobHierarchyOrder,
  UNMAPPED_GEO_KEY,
} from 'partner360/features/financialCockpit/lib'
import { isEmptyMetricValue } from 'partner360/features/financialCockpit/ui/cockpitRevenueAndSob/lib/metricPresentation'

import type { ICockpitExportCell } from '../../../api'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../configs/translations'
import { CockpitTableColumnTitle } from '../cockpitTableTitle'

import type { IHierarchyExportColumn } from './getHierarchyExportTable'
import { ICockpitPartnerLinkParams } from './useCockpitPartnerLinkParams'
import { IHierarchyControls } from './useHierarchyExpansion'

import styles from './getHierarchyDimensionColumns.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.revenueAndSob
/** The Geo axis root's own name — the same "Geo Mapping" concept
 *  `cockpitGeoMappingFilter/CockpitGeoMappingFilter.tsx` already labels its filter with, not a
 *  generic "Geo" repeat of the column title. */
const GEO_MAPPING_LABEL = FINANCIAL_COCKPIT_TRANSLATION_KEYS.geoMapping.label

const isGeoLevel = (level: TRevenueAndSobHierarchyLevel): level is 'geo' => level === 'geo'

const isPartnerLevel = (level: TRevenueAndSobHierarchyLevel): level is 'partner' =>
  level === 'partner'

const isProductLevel = (
  level: TRevenueAndSobHierarchyLevel
): level is 'offering' | 'category' | 'subCategory' | 'l3Category' =>
  level === 'offering' || level === 'category' || level === 'subCategory' || level === 'l3Category'

const PRODUCT_DEPTH: Record<'offering' | 'category' | 'subCategory' | 'l3Category', number> = {
  offering: 1,
  category: 2,
  subCategory: 3,
  l3Category: 4,
}
const DEPTH_CLASS: Record<number, string> = {
  1: styles.depth1,
  2: styles.depth2,
  3: styles.depth3,
  4: styles.depth4,
  5: styles.depth5,
  6: styles.depth6,
}
/** The Geo axis has no fixed number of levels (`geoMappingItems` is an arbitrary-depth tree), so a
 *  node deeper than the deepest indent class just reuses that class rather than losing its indent. */
const MAX_INDENT_DEPTH = 6

/** Partner names are the one dimension label with no upper bound on length, and the column's CSS
 *  ellipsis can't fire in an auto-layout table — nothing constrains the cell's width, and
 *  `white-space: nowrap` pins its min-content to the whole name — so a long one widens the table
 *  instead of clipping. Cut by character count instead; the full name stays on the hover card. */
const PARTNER_NAME_MAX_LENGTH = 25

/** What a dimension column shows for one row — either a translated root toggle (`rootKey`, an
 *  off-axis node repeating the other axis's root), an empty off-axis cell (no subtree to open), or
 *  a leaf/branch cell at some indentation `depth`. */
interface IDimensionCell {
  rootKey?: string
  depth: number
  hasChildren: boolean
  isExpanded: boolean
  isEmpty: boolean
}

const EMPTY_CELL: IDimensionCell = {
  depth: 0,
  hasChildren: false,
  isExpanded: false,
  isEmpty: true,
}

const asRootCell = (rootKey: string, isExpanded: boolean): IDimensionCell => ({
  rootKey,
  depth: 0,
  hasChildren: true,
  isExpanded,
  isEmpty: false,
})

const asLeafCell = (depth: number, hasChildren: boolean, isExpanded: boolean): IDimensionCell => ({
  depth,
  hasChildren,
  isExpanded,
  isEmpty: false,
})

/** A Partner leaf, rendered in the Geo column — the axis it hangs off the end of, and the only one
 *  that grows partners. Childless *there*: a partner is the last thing the Geo axis breaks down
 *  into. (It may still open into a Product subtree, but that caret lives in the other column — see
 *  `getProductCell`.) Its `partnerDepth` already counts one past the geo node it hangs under, and is
 *  clamped here the same way `geoDepth` is. */
const asPartnerCell = (item: IRevenueAndSobHierarchyNode): IDimensionCell =>
  asLeafCell(Math.min(item.partnerDepth, MAX_INDENT_DEPTH), false, false)

/** The Geo column: a Geo node renders as itself, indented by its own `geoDepth`; a Product node
 *  that also has its own Geo subtree (under `productGeo` order) shows the Geo root toggle repeated;
 *  a Partner leaf renders its name under the geo node it belongs to. This is the only column that
 *  ever names a partner. */
const getGeoCell = (
  item: IRevenueAndSobHierarchyNode,
  isExpanded: boolean,
  isExpandable: boolean
): IDimensionCell => {
  if (isPartnerLevel(item.level)) {
    return asPartnerCell(item)
  }

  if (!isGeoLevel(item.level)) {
    /** Under Product > Geo each product opens into its own geo tree, so the root repeats there. */
    return isExpandable ? asRootCell(GEO_MAPPING_LABEL, isExpanded) : EMPTY_CELL
  }

  return asLeafCell(
    Math.min(item.geoDepth + 1, MAX_INDENT_DEPTH),
    isExpandable,
    isExpandable && isExpanded
  )
}

/** The Product column: Offering/Category/SubCategory/L3Category nodes render as themselves; a Geo
 *  node that also has its own Product subtree (under `geoProduct` order) shows the Product root
 *  toggle repeated. It never names a Partner — partners hang off the Geo axis alone — but it does
 *  carry the toggle into a partner's own offerings. */
const getProductCell = (
  item: IRevenueAndSobHierarchyNode,
  isExpanded: boolean,
  isExpandable: boolean
): IDimensionCell => {
  if (isPartnerLevel(item.level)) {
    /** A Partner at the bottom of a Geo drill opens into its own offerings, so it repeats the
     *  Product root exactly as the Geo nodes above it do. Only the leaves the builder actually grew
     *  a subtree under are expandable — the rest keep the empty cell. */
    return isExpandable ? asRootCell(KEYS.offering, isExpanded) : EMPTY_CELL
  }

  if (!isProductLevel(item.level)) {
    /** Under Geo > Product each geo node opens into its own offerings, so the root repeats there. */
    return isExpandable ? asRootCell(KEYS.offering, isExpanded) : EMPTY_CELL
  }

  return asLeafCell(PRODUCT_DEPTH[item.level], isExpandable, isExpandable && isExpanded)
}

/** Geo nodes label with their short name (`NE`, `DA`, `DE`) — the Geo column is the widest dimension
 *  cell and the mapping payload already carries a short form for every node.
 *
 *  The full name rides along as a native `title` rather than a `Tooltip`: the Geo filter beside this
 *  table still lists full names, so an abbreviation has to be resolvable without leaving the row,
 *  but this is a dense, deeply indented cell where a hover card would fight the caret. */
const makeRenderGeoLabel =
  (geoShortNameById: Map<string, string>) => (item: IRevenueAndSobHierarchyNode) => {
    if (item.geoKey === UNMAPPED_GEO_KEY) {
      return <Translation text={KEYS.unmappedGeo} span />
    }

    const shortName = geoShortNameById.get(item.geoKey)

    /** A key the mapping no longer covers keeps the row's own full label, untitled — there is no
     *  second name to reveal. */
    return shortName ? <span title={item.geoLabel}>{shortName}</span> : item.geoLabel
  }

/** The Geo label as plain text, for the export. Mirrors `makeRenderGeoLabel`: the short name is
 *  what the cell shows, so it is what the deck repeats; the full name lives only on hover. */
const getGeoLabelText = (
  item: IRevenueAndSobHierarchyNode,
  geoShortNameById: Map<string, string>,
  translate: (key: string) => string
) => {
  if (item.geoKey === UNMAPPED_GEO_KEY) {
    return translate(KEYS.unmappedGeo)
  }

  return geoShortNameById.get(item.geoKey) ?? item.geoLabel
}

/** Offering codes (`pc`, `supplies`, ...) aren't i18n keys, unlike every other product level
 *  (category/subCategory/l3Category are all `jbp-*` taxonomy keys) — resolved through
 *  `offeringNameByCode` instead, the lookup `useOfferingNameByCode` builds off the offerings
 *  catalog. */
const makeRenderProductLabel =
  (offeringNameByCode: Map<ProgramTypes, string>) => (item: IRevenueAndSobHierarchyNode) =>
    item.level === 'offering' ? (
      (offeringNameByCode.get(item.productKey as ProgramTypes) ?? item.productKey)
    ) : (
      <Translation text={item.productKey} span />
    )

/** Mirrors `makeRenderProductLabel`. */
const getProductLabelText = (
  item: IRevenueAndSobHierarchyNode,
  offeringNameByCode: Map<ProgramTypes, string>,
  translate: (key: string) => string
) =>
  item.level === 'offering'
    ? (offeringNameByCode.get(item.productKey as ProgramTypes) ?? item.productKey)
    : translate(item.productKey)

/** Partner ids aren't i18n keys either — resolved through `partnerNameById` (`usePartnerNameById`),
 *  falling back to the raw id so a partner missing from the map still reads as something.
 *
 *  Rendered through the app's canonical `PartnerNameWithLink`, so a partner reached by drilling the
 *  cockpit opens its own plan page in a new tab, on the fiscal year and offering the cockpit is
 *  currently showing (`useCockpitPartnerLinkParams`).
 *
 *  Cut to `PARTNER_NAME_MAX_LENGTH` so one long name can't widen the whole table, with the full name
 *  handed to that component's own hover card rather than a second one. The cut happens after the id
 *  fallback, since a raw `PartnerId` can be longer than the budget too — and never touches
 *  `item.partnerId`, which is what the link is built from. */
const makeRenderPartnerLabel =
  (partnerNameById: Map<string, string>, partnerLinkParams: ICockpitPartnerLinkParams) =>
  (item: IRevenueAndSobHierarchyNode) => {
    const partnerName = partnerNameById.get(item.partnerId) || item.partnerId
    const isTruncated = partnerName.length > PARTNER_NAME_MAX_LENGTH

    return (
      <PartnerNameWithLink
        className={styles.partnerLink}
        partnerId={item.partnerId}
        partnerName={
          isTruncated ? `${partnerName.slice(0, PARTNER_NAME_MAX_LENGTH)}...` : partnerName
        }
        /** Only when the cell isn't already showing the whole thing. */
        tipTitle={isTruncated ? partnerName : undefined}
        fYear={partnerLinkParams.fYear}
        programType={partnerLinkParams.offering}
      />
    )
  }

/** Mirrors `makeRenderPartnerLabel` — except for the length cut, which is a workaround for an
 *  auto-layout HTML table and has no meaning in a deck that fits text to its own column width. */
const getPartnerLabelText = (
  item: IRevenueAndSobHierarchyNode,
  partnerNameById: Map<string, string>
) => partnerNameById.get(item.partnerId) || item.partnerId

/** One dimension cell as the export reads it: the label the column shows, plus the nesting state
 *  the caret expresses. Built from the very `IDimensionCell` the renderer draws. */
const toDimensionExportCell = (
  cell: IDimensionCell,
  getLabel: () => string,
  translate: (key: string) => string
): ICockpitExportCell => ({
  text: cell.rootKey ? translate(cell.rootKey) : cell.isEmpty ? '' : getLabel(),
  depth: cell.depth,
  hasChildren: cell.hasChildren,
  isExpanded: cell.isExpanded,
})

/** The Geo column carries two kinds of row: its own Geo nodes, and the Partner leaves hanging off
 *  the end of that axis. The Product column needs no such wrapper — a Partner row's cell there is
 *  always a root toggle or `EMPTY_CELL`, both of which `renderDimension` short-circuits on before
 *  calling any label renderer. */
const withPartnerLeafLabel =
  (
    renderAxisLabel: (item: IRevenueAndSobHierarchyNode) => ReactNode,
    renderPartnerLabel: (item: IRevenueAndSobHierarchyNode) => ReactNode
  ) =>
  (item: IRevenueAndSobHierarchyNode) =>
    isPartnerLevel(item.level) ? renderPartnerLabel(item) : renderAxisLabel(item)

const renderHierarchyRoot = (
  item: IRevenueAndSobHierarchyNode,
  translationKey: string,
  isExpanded: boolean,
  onToggle: (rowId: string) => void
) => (
  <button
    type="button"
    className={styles.toggle}
    onClick={() => onToggle(item.rowId)}
    aria-expanded={isExpanded}
  >
    <span className={styles.caret} aria-hidden="true" />
    <b>
      <Translation text={translationKey} span />
    </b>
  </button>
)

const renderDimension = (
  item: IRevenueAndSobHierarchyNode,
  cell: IDimensionCell,
  renderLabel: (item: IRevenueAndSobHierarchyNode) => ReactNode,
  onToggle: (rowId: string) => void
) => {
  if (cell.rootKey) {
    return renderHierarchyRoot(item, cell.rootKey, cell.isExpanded, onToggle)
  }

  if (cell.isEmpty) {
    return <span aria-hidden="true" />
  }

  const indentClass = DEPTH_CLASS[cell.depth]

  if (!cell.hasChildren) {
    return <span className={classNames(styles.childLabel, indentClass)}>{renderLabel(item)}</span>
  }

  return (
    <button
      type="button"
      className={classNames(styles.toggle, indentClass)}
      onClick={() => onToggle(item.rowId)}
      aria-expanded={cell.isExpanded}
    >
      <span className={styles.caret} aria-hidden="true" />
      <span className={styles.rootLabel}>{renderLabel(item)}</span>
    </button>
  )
}

const renderTitle = (title: string) => (
  <CockpitTableColumnTitle>
    <Translation text={title} span />
  </CockpitTableColumnTitle>
)

/** Whether a row has a caret on either axis — the two hierarchy tables share this as their
 *  "is a branch" test, which is also what decides whether the row's value cells are bold. */
export const isExpandableRow = (controls: IHierarchyControls, rowId: string) =>
  controls.primaryExpandableRows.has(rowId) || controls.secondaryExpandableRows.has(rowId)

/** A calculated `0` reads as "nothing behind this node" in a hierarchy table, so it shows the
 *  same placeholder as genuinely missing data — unlike the cockpit tiles, which still render a
 *  concrete `$0`/`0,0%` from the shared formatters. A row whose every column comes out empty this
 *  way is dropped altogether, by `getPopulatedHierarchyRows`. */
export const withZeroAsEmpty =
  (format: (value: number | null) => string) => (value: number | null) =>
    isEmptyMetricValue(value) ? EMPTY_COCKPIT_VALUE : format(value)

/** Partner rows are excluded even once they grow a Product subtree: they are the detail at the
 *  bottom of a geo drill, and a country's worth of them set in bold would drown out the branches
 *  above. */
export const isRowBold = (item: IRevenueAndSobHierarchyNode, isExpandable: boolean) =>
  item.parentRowId === null || (isExpandable && !isPartnerLevel(item.level))

/** Roots and branches are bold; leaves and Partner rows are not. */
export const renderHierarchyValue = (
  item: IRevenueAndSobHierarchyNode,
  value: string,
  isExpandable: boolean
): ReactElement | string => (isRowBold(item, isExpandable) ? <b>{value}</b> : value)

interface IParams {
  /** Whether the Geo column is shown at all — manager cockpit mode only. */
  showGeoHierarchy: boolean
  /** Which axis is outer. Meaningless when `showGeoHierarchy` is `false` — the caller pins this to
   *  `'productGeo'` in that case, which is what makes every (product-only) edge classify as
   *  primary in `useHierarchyExpansion`. */
  hierarchyOrder: TRevenueAndSobHierarchyOrder
  controls: IHierarchyControls
  /** Short display name for each geo `itemId`, keyed the way a geo row's `geoKey` already is — what
   *  the Geo column labels its nodes with, full name on hover. See `useGeoShortNameById`. */
  geoShortNameById: Map<string, string>
  /** Display name for each offering code — the only product level whose own code isn't already a
   *  `jbp-*` i18n key (see `makeRenderProductLabel`). */
  offeringNameByCode: Map<ProgramTypes, string>
  /** Display name for each partner id, for the Partner leaves the Geo column renders. Empty in
   *  partner cockpit mode, which grows no such leaves. */
  partnerNameById: Map<string, string>
  /** Cockpit context each Partner leaf's link carries into the partner's own page — see
   *  `useCockpitPartnerLinkParams`. */
  partnerLinkParams: ICockpitPartnerLinkParams
  /** Resolves the i18n keys the dimension cells show, for the export's plain-text copy of them. */
  translate: (key: string) => string
}

/** The leading Geo and/or Product columns every hierarchy table starts with: the caret toggles,
 *  the per-level indentation and the label resolution, ordered by `hierarchyOrder`. Each axis
 *  contributes a pair — a header-only parent spacer plus the dimension cell itself — because the
 *  table's two-line header is expressed through `parentField`.
 *
 *  Generic over the row type so the Revenue & SOB and Plan Quality tables get literally the same
 *  nesting UI on top of their own value columns. */
export const getHierarchyDimensionColumns = <T extends IRevenueAndSobHierarchyNode>({
  showGeoHierarchy,
  hierarchyOrder,
  controls,
  geoShortNameById,
  offeringNameByCode,
  partnerNameById,
  partnerLinkParams,
  translate,
}: IParams): IHierarchyExportColumn<T>[] => {
  const {
    expandedPrimaryRows,
    expandedSecondaryRows,
    primaryExpandableRows,
    secondaryExpandableRows,
    togglePrimaryRow,
    toggleSecondaryRow,
  } = controls

  const renderPartnerLabel = makeRenderPartnerLabel(partnerNameById, partnerLinkParams)

  const isGeoPrimary = hierarchyOrder === 'geoProduct'
  const geoExpandedRows = isGeoPrimary ? expandedPrimaryRows : expandedSecondaryRows
  const geoExpandableRows = isGeoPrimary ? primaryExpandableRows : secondaryExpandableRows
  const toggleGeoRow = isGeoPrimary ? togglePrimaryRow : toggleSecondaryRow
  const productExpandedRows = isGeoPrimary ? expandedSecondaryRows : expandedPrimaryRows
  const productExpandableRows = isGeoPrimary ? secondaryExpandableRows : primaryExpandableRows
  const toggleProductRow = isGeoPrimary ? toggleSecondaryRow : togglePrimaryRow

  const getGeo = (item: T) =>
    getGeoCell(item, geoExpandedRows.has(item.rowId), geoExpandableRows.has(item.rowId))
  const getProduct = (item: T) =>
    getProductCell(item, productExpandedRows.has(item.rowId), productExpandableRows.has(item.rowId))

  /** Only the Geo column names a partner, so only it takes the Partner-aware wrapper. */
  const renderGeoDimensionLabel = withPartnerLeafLabel(
    makeRenderGeoLabel(geoShortNameById),
    renderPartnerLabel
  )
  const renderProductLabel = makeRenderProductLabel(offeringNameByCode)

  const geoColumns: IHierarchyExportColumn<T>[] = [
    {
      field: 'geoParent',
      title: '',
      className: classNames(styles.groupHeader, styles.parentHeader),
      hideSettings: true,
      disableSort: true,
      titleRender: () => <></>,
      renderFunction: () => <></>,
    },
    {
      field: 'geoDimension',
      parentField: 'geoParent',
      title: KEYS.geo,
      className: styles.dimension,
      hideSettings: true,
      disableSort: true,
      titleRender: () => renderTitle(KEYS.geo),
      renderFunction: (item) =>
        renderDimension(item, getGeo(item), renderGeoDimensionLabel, toggleGeoRow),
      exportCell: (item) =>
        toDimensionExportCell(
          getGeo(item),
          () =>
            isPartnerLevel(item.level)
              ? getPartnerLabelText(item, partnerNameById)
              : getGeoLabelText(item, geoShortNameById, translate),
          translate
        ),
    },
  ]

  const productColumns: IHierarchyExportColumn<T>[] = [
    {
      field: 'productParent',
      title: '',
      className: classNames(styles.groupHeader, styles.parentHeader),
      hideSettings: true,
      disableSort: true,
      titleRender: () => <></>,
      renderFunction: () => <></>,
    },
    {
      field: 'productDimension',
      parentField: 'productParent',
      title: KEYS.product,
      className: styles.dimension,
      hideSettings: true,
      disableSort: true,
      titleRender: () => renderTitle(KEYS.product),
      renderFunction: (item) =>
        renderDimension(item, getProduct(item), renderProductLabel, toggleProductRow),
      exportCell: (item) =>
        toDimensionExportCell(
          getProduct(item),
          () => getProductLabelText(item, offeringNameByCode, translate),
          translate
        ),
    },
  ]

  if (!showGeoHierarchy) {
    return productColumns
  }

  return isGeoPrimary ? [...geoColumns, ...productColumns] : [...productColumns, ...geoColumns]
}
